import { useState } from "react";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

const TOPICS = [
  { value: "general", label: "Consulta general" },
  { value: "press", label: "Prensa / Sala de redacción" },
  { value: "advertising", label: "Anunciantes / Pauta" },
  { value: "tip", label: "Enviar un dato / Tip" },
  { value: "bug", label: "Reportar un error" },
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    topic: "general",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/contact", form);
      setDone(true);
      if (res.data.email_sent === false) {
        toast.success("Mensaje recibido. Te respondemos pronto.");
      } else {
        toast.success("¡Mensaje enviado! Te respondemos pronto.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No pudimos enviar tu mensaje. Probá de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-10 md:py-16" data-testid="contact-page">
        <p className="overline accent-text mb-2">CONTACTO</p>
        <h1 className="font-headline uppercase text-3xl md:text-5xl leading-tight mb-3">
          Hablemos
        </h1>
        <p className="text-base md:text-lg mb-10" style={{ color: "var(--text-muted)" }}>
          ¿Tenés un dato, una nota, una propuesta comercial o querés decirnos algo?
          Escribinos y te respondemos lo antes posible.
        </p>

        {done ? (
          <div
            className="p-8 rounded-sm border text-center"
            style={{ borderColor: "var(--border)", background: "var(--surface)" }}
            data-testid="contact-success"
          >
            <CheckCircle2 size={48} className="mx-auto mb-4 accent-text" />
            <h2 className="font-headline text-2xl uppercase mb-2">¡Listo!</h2>
            <p style={{ color: "var(--text-muted)" }}>
              Recibimos tu mensaje. Te respondemos a <strong>{form.email}</strong> en las próximas 24-48hs.
            </p>
            <button
              onClick={() => { setDone(false); setForm({ name: "", email: "", subject: "", message: "", topic: "general" }); }}
              className="btn-ghost mt-6"
              data-testid="contact-send-another"
            >
              Enviar otro mensaje
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-1.5">Nombre *</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 border rounded-sm bg-transparent outline-none focus:border-[color:var(--accent)] transition"
                  style={{ borderColor: "var(--border)" }}
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="overline block mb-1.5">Email *</label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 border rounded-sm bg-transparent outline-none focus:border-[color:var(--accent)] transition"
                  style={{ borderColor: "var(--border)" }}
                  data-testid="contact-email"
                />
              </div>
            </div>
            <div>
              <label className="overline block mb-1.5">Tema</label>
              <select
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                className="w-full px-4 py-3 border rounded-sm bg-transparent outline-none focus:border-[color:var(--accent)] transition"
                style={{ borderColor: "var(--border)" }}
                data-testid="contact-topic"
              >
                {TOPICS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-1.5">Asunto *</label>
              <input
                required
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="w-full px-4 py-3 border rounded-sm bg-transparent outline-none focus:border-[color:var(--accent)] transition"
                style={{ borderColor: "var(--border)" }}
                data-testid="contact-subject"
              />
            </div>
            <div>
              <label className="overline block mb-1.5">Mensaje *</label>
              <textarea
                required
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-3 border rounded-sm bg-transparent outline-none focus:border-[color:var(--accent)] transition resize-none"
                style={{ borderColor: "var(--border)" }}
                data-testid="contact-message"
              />
              <p className="text-xs mt-1.5" style={{ color: "var(--text-muted)" }}>
                Mínimo 10 caracteres
              </p>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-accent disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="contact-submit"
            >
              {loading ? "Enviando..." : "Enviar mensaje"}
              <Send size={14} />
            </button>
          </form>
        )}

        <div className="mt-12 pt-8 border-t" style={{ borderColor: "var(--border)" }}>
          <p className="overline mb-3">OTRAS FORMAS</p>
          <div className="flex flex-wrap items-center gap-4 text-sm" style={{ color: "var(--text-muted)" }}>
            <span className="flex items-center gap-2"><Mail size={14} /> contacto@popu.news</span>
            <a href="https://www.influcastbo.com" target="_blank" rel="noreferrer" className="accent-text hover:underline">
              www.influcastbo.com
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
